/**
 * The files-mode transfer requests (Tasks 7.14 / 7.15) over the S7 channels: `file-download` (the save dialog for one
 * file, the folder picker otherwise — main picks, P5), `file-upload` (the pickers, or dropped paths), `file-delete` and
 * `directory-create`. Each request id is tracked with the tab that asked, so closing the tab cancels a request still
 * waiting in main (a picker, planning); outcomes are S2's `StorageJobOutcome` shapes and a refusal shows main's
 * message. Analytics carry enums only (`service: 'file'`, the source, the protocol), never a share or path.
 */
import { toast } from '../../../../components/ui/toaster';
import type {
  DirectoryRef, FileRef, FileServiceRef, FileShareProtocolState, ShareRef, StorageOperationError, TransferCollisionPolicy,
} from '../../../../types/azureStorage';
import { useTransfersStore } from '../../../transfers/store/transfersStore';
import {
  cancelStorageRequest, createShareDirectory, deleteFileResources, downloadFileResources, newStorageRequestId, toStorageOperationError, uploadFileResources,
} from '../services/azureStorageApi';
import { trackStorageEvent } from '../services/storageAnalytics';
import type { StorageDownloadOutcome } from '../services/storageDownload';
import type { StorageMutationSource } from '../mutations/storageMutations';
import { startShareRequest } from './shareApi';

/** Upload / delete requests own their planning in main (S2's rule): the timeout is the request's idle deadline. */
const TRANSFER_REQUEST_TIMEOUT_MS = 120_000;
const SNAPSHOT_COUNT_TIMEOUT_MS = 15_000;

export type FileJobOutcome = StorageDownloadOutcome;
export type ShareSnapshotCount = { state: 'known'; count: number } | { state: 'unknown' };

const pendingByTab = new Map<string, Set<string>>();

/** Runs `send` with a request id tracked under `tabId` until it answers. */
export async function withTabRequest<T>(tabId: string | undefined, send: (requestId: string) => Promise<T>): Promise<T> {
  const requestId = newStorageRequestId();
  const pending = tabId ? pendingByTab.get(tabId) ?? new Set<string>() : undefined;
  if (tabId && pending) pendingByTab.set(tabId, pending.add(requestId));
  try {
    return await send(requestId);
  } finally {
    pending?.delete(requestId);
    if (tabId && pending?.size === 0 && pendingByTab.get(tabId) === pending) pendingByTab.delete(tabId);
  }
}

/** The files tab went away: its unanswered transfer requests stop in main (no picker, planning or job follows). */
export function cancelFileTabRequests(tabId: string): void {
  const pending = pendingByTab.get(tabId);
  pendingByTab.delete(tabId);
  for (const requestId of pending ?? []) void cancelStorageRequest(requestId);
}

export async function requestFileDownload(args: { connectionId: string; tabId?: string; resources: FileServiceRef[] }): Promise<StorageDownloadOutcome> {
  if (args.resources.length === 0) return { kind: 'dialog-cancelled' };
  trackStorageEvent('toolbar:storage_download', { service: 'file' });
  try {
    const result = await withTabRequest(args.tabId, (requestId) => downloadFileResources({ connectionId: args.connectionId, resources: args.resources, requestId }));
    if (!result.started) return { kind: 'dialog-cancelled' };
    useTransfersStore.getState().noteJobSource(result.jobId, 'toolbar');
    return { kind: 'started', jobId: result.jobId };
  } catch (raw) {
    const error = toStorageOperationError(raw);
    if (error.category === 'cancelled') return { kind: 'cancelled' };
    toast.error({ title: 'Download failed', description: error.message });
    return { kind: 'error', message: error.message };
  }
}

function failed(title: string, raw: unknown): FileJobOutcome {
  const error = toStorageOperationError(raw);
  if (error.category === 'cancelled') return { kind: 'cancelled' };
  toast.error({ title, description: error.message });
  return { kind: 'error', message: error.message };
}

export async function requestFileUpload(args: {
  connectionId: string;
  tabId?: string;
  destination: ShareRef | DirectoryRef;
  sources?: string[];
  sourceKind?: 'files' | 'folder';
  collision?: TransferCollisionPolicy;
  source: StorageMutationSource;
}): Promise<FileJobOutcome> {
  try {
    const result = await withTabRequest(args.tabId, (requestId) => uploadFileResources({
      connectionId: args.connectionId, destination: args.destination, requestId, timeoutMs: TRANSFER_REQUEST_TIMEOUT_MS,
      ...(args.sources ? { sources: args.sources } : {}),
      ...(args.sourceKind ? { sourceKind: args.sourceKind } : {}),
      ...(args.collision ? { collision: args.collision } : {}),
    }));
    if (!result.started) return { kind: 'dialog-cancelled' };
    useTransfersStore.getState().noteJobSource(result.jobId, args.source);
    trackStorageEvent('feature:storage_upload', {
      source: args.source, service: 'file', collision: args.collision ?? 'ask',
      kind: args.sourceKind ?? ((args.sources?.length ?? 0) > 1 ? 'multiple' : 'single'),
    });
    return { kind: 'started', jobId: result.jobId };
  } catch (raw) {
    return failed('Upload failed', raw);
  }
}

export async function deleteFileSelection(args: { connectionId: string; tabId?: string; resources: Array<DirectoryRef | FileRef>; source: StorageMutationSource }): Promise<FileJobOutcome> {
  try {
    const result = await withTabRequest(args.tabId, (requestId) => deleteFileResources({ connectionId: args.connectionId, resources: args.resources, requestId, timeoutMs: TRANSFER_REQUEST_TIMEOUT_MS }));
    if (!result.started) return { kind: 'dialog-cancelled' };
    useTransfersStore.getState().noteJobSource(result.jobId, args.source);
    trackStorageEvent('feature:storage_delete', { source: args.source, kind: 'items', service: 'file' });
    return { kind: 'started', jobId: result.jobId };
  } catch (raw) {
    return failed('Delete failed', raw);
  }
}

type FileActionOutcome = 'ok' | 'refused' | 'error' | 'cancelled';
const REFUSALS = new Set<StorageOperationError['category']>(['authentication', 'authorization', 'not_found', 'conflict', 'invalid_input', 'unsupported']);
const outcomeOf = (error: StorageOperationError): FileActionOutcome =>
  (error.category === 'cancelled' ? 'cancelled' : REFUSALS.has(error.category) ? 'refused' : 'error');

/** New folder (a REAL directory, unlike blob virtual folders); fires `feature:storage_file_action` once with its outcome. */
export async function createFileDirectory(args: {
  connectionId: string;
  tabId?: string;
  directory: DirectoryRef;
  source: 'toolbar' | 'row-menu' | 'empty-state';
  protocol: FileShareProtocolState;
}): Promise<{ ok: true } | { ok: false; error: StorageOperationError }> {
  const track = (outcome: FileActionOutcome) =>
    trackStorageEvent('feature:storage_file_action', { action: 'new_folder', outcome, source: args.source, protocol: args.protocol });
  try {
    await withTabRequest(args.tabId, (requestId) => createShareDirectory({ connectionId: args.connectionId, resource: args.directory, requestId, timeoutMs: 30_000 }));
    track('ok');
    return { ok: true };
  } catch (raw) {
    const error = toStorageOperationError(raw);
    track(outcomeOf(error));
    return { ok: false, error };
  }
}

/** The share's snapshot count for the delete confirmation; any refusal or failure is "unknown". */
export async function readShareSnapshotCount(connectionId: string, share: ShareRef): Promise<ShareSnapshotCount> {
  const answer = await startShareRequest('shareSnapshots', { connectionId, resource: share, timeoutMs: SNAPSHOT_COUNT_TIMEOUT_MS }).promise;
  return answer.ok ? { state: 'known', count: answer.value.snapshots.length } : { state: 'unknown' };
}
